import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import MonetizationOnOutlined from "@mui/icons-material/MonetizationOnOutlined";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { prettyPrintDateTime } from "util/dates";

interface SubmissionPaymentProps {
  submissionPayments: any[];
  isEditMode: boolean;
  onAddPayment: () => void;
  onOpenPayment: (paymentId: string) => void;
}

export function SubmissionPayment({
  submissionPayments,
  isEditMode,
  onAddPayment,
  onOpenPayment,
}: SubmissionPaymentProps) {
  const theme = useTheme();

  if (!isEditMode) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiTableCell-root": {
            borderColor: theme.palette.divider,
          },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.grey50.main,
              }}
            >
              <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Payee</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Payment Date</TableCell>
              <TableCell sx={{ fontWeight: 500 }} align="right">
                Amount
              </TableCell>
              <TableCell sx={{ width: 100 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submissionPayments.length > 0 ? (
              <>
                {submissionPayments.map((submissionPayment: any) => (
                  <TableRow
                    key={submissionPayment.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => onOpenPayment(submissionPayment.payment.id)}
                  >
                    <TableCell>
                      {submissionPayment.payment.status ? (
                        <Typography variant="body2" color="text.primary">
                          {prettyPrintEnumValue(submissionPayment.payment.status)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {submissionPayment.payment.payee || "—"}
                    </TableCell>
                    <TableCell>
                      {submissionPayment.payment.paidAt
                        ? prettyPrintDateTime(submissionPayment.payment.paidAt)
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {submissionPayment.payment.amount
                        ? new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(submissionPayment.payment.amount)
                        : "—"}
                    </TableCell>
                    <TableCell
                      sx={{ width: 100, whiteSpace: "nowrap" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {/* Future: Add edit/delete buttons here */}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              <TableRow hover sx={{ cursor: "pointer" }} onClick={onAddPayment}>
                <TableCell colSpan={5}>
                  <Button
                    startIcon={<MonetizationOnOutlined />}
                    variant="text"
                    color="primary"
                    sx={{ width: "100%", justifyContent: "flex-start" }}
                  >
                    Add Payment
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}